/**
 * Minimal client-side delimited-file parser (CSV / TSV) for the Assistant's
 * create-from-file flow. Parsing happens in the browser. Handles quoted fields,
 * escaped quotes ("") and embedded newlines. XLSX is not handled (export CSV).
 */
export interface ParsedFile {
  filename: string
  headers: string[]
  rows: Record<string, unknown>[]
}

function splitRows(text: string, delim: string): string[][] {
  const rows: string[][] = []
  let field = ''
  let row: string[] = []
  let inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++ } else inQuotes = false
      } else field += ch
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === delim) {
      row.push(field); field = ''
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = ''
    } else if (ch === '\r') {
      /* skip */
    } else field += ch
  }
  if (field.length || row.length) { row.push(field); rows.push(row) }
  return rows.filter((r) => r.some((c) => c.trim() !== ''))
}

export function parseDelimited(text: string, filename: string): ParsedFile {
  const delim = filename.toLowerCase().endsWith('.tsv') || (text.indexOf('\t') >= 0 && text.indexOf(',') < 0) ? '\t' : ','
  const grid = splitRows(text, delim)
  if (!grid.length) return { filename, headers: [], rows: [] }
  const headers = grid[0].map((h) => h.trim())
  const rows = grid.slice(1).map((cells) => {
    const r: Record<string, unknown> = {}
    headers.forEach((h, i) => { r[h] = cells[i] ?? '' })
    return r
  })
  return { filename, headers, rows }
}

export async function parseFile(file: File): Promise<ParsedFile> {
  const name = file.name.toLowerCase()
  if (!(name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt'))) {
    throw new Error('Please upload a CSV or TSV file (export your spreadsheet as CSV).')
  }
  const text = await file.text()
  return parseDelimited(text, file.name)
}
